import React, { forwardRef, useState, useEffect } from 'react';
import {
  Paper,
  Group,
  Text,
  Badge,
  Stack,
  useComputedColorScheme,
  ActionIcon,
} from '@mantine/core';
import { Competency, Swimlane, UserCompetency, CardSize } from '../../types';
import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd';
import { IconCheck, IconPencil, IconSend } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { formatDateShort } from '../../utils/date';

interface CompetencyCardProps {
  comp: Competency;
  gradeIdx: number;
  swimlane: Swimlane;
  onClick: () => void;
  dragProps: DraggableProvidedDraggableProps | Record<string, never>;
  dragHandleProps: DraggableProvidedDragHandleProps | null | Record<string, never>;
  isDragging: boolean;
  style?: React.CSSProperties;
  userHas?: boolean;
  userCompetency?: UserCompetency;
  isDragDropEnabled?: boolean;
  cardSize?: CardSize;
  onEdit?: () => void;
  onRequestReview?: () => void;
  isRequired?: boolean;
}

const CompetencyCardComponent = forwardRef<HTMLDivElement, CompetencyCardProps>(
  (
    {
      comp,
      onClick,
      dragProps,
      dragHandleProps,
      isDragging,
      style,
      userHas,
      userCompetency,
      isDragDropEnabled = true,
      cardSize = 'L',
      onEdit,
      onRequestReview,
      isRequired = false,
    },
    ref,
  ) => {
    const [showDescription, setShowDescription] = useState(cardSize === 'L');
    const [showTags, setShowTags] = useState(cardSize === 'M' || cardSize === 'L');
    const colorScheme = useComputedColorScheme('light');
    const { i18n, t } = useTranslation();
    const isDark = colorScheme === 'dark';

    useEffect(() => {
      const shouldShowDescription = cardSize === 'L';
      const shouldShowTags = cardSize === 'M' || cardSize === 'L';

      if (shouldShowDescription !== showDescription) {
        setShowDescription(shouldShowDescription);
      }
      if (shouldShowTags !== showTags) {
        setShowTags(shouldShowTags);
      }
    }, [cardSize, showDescription, showTags]);

    const handleEditClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onEdit) {
        onEdit();
      }
    };

    const handleRequestReviewClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onRequestReview) {
        onRequestReview();
      }
    };

    return (
      <Paper
        ref={ref}
        {...(isDragDropEnabled ? dragProps : {})}
        {...(isDragDropEnabled ? dragHandleProps : {})}
        shadow={isDragging ? 'md' : 'xs'}
        p="sm"
        style={{
          background: isDark ? 'var(--mantine-color-dark-6)' : '#fff',
          border: userHas
            ? `2px solid ${isDark ? 'var(--mantine-color-teal-6)' : '#4caf50'}`
            : isRequired
              ? `1px solid ${isDark ? 'var(--mantine-color-yellow-6)' : '#ff9800'}`
              : `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : '#ececec'}`,
          borderRadius: 8,
          cursor: isDragDropEnabled ? 'grab' : 'pointer',
          fontSize: 13,
          position: 'relative',
          color: isDark ? 'var(--mantine-color-gray-1)' : undefined,
          transition:
            'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
          ...style,
        }}
        onClick={onClick}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isDark
              ? '0 8px 30px rgba(0, 0, 0, 0.4), 0 4px 15px rgba(0, 0, 0, 0.2)'
              : '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 15px rgba(0, 0, 0, 0.08)';
            e.currentTarget.style.borderColor = userHas
              ? isDark
                ? 'var(--mantine-color-teal-5)'
                : '#45a049'
              : isRequired
                ? isDark
                  ? 'var(--mantine-color-yellow-5)'
                  : '#f57c00'
                : isDark
                  ? 'var(--mantine-color-dark-3)'
                  : '#d4d4d4';
            e.currentTarget.style.backgroundColor = isDark
              ? 'var(--mantine-color-dark-5)'
              : '#fafafa';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.transform = 'translateY(0px)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.borderColor = userHas
              ? isDark
                ? 'var(--mantine-color-teal-6)'
                : '#4caf50'
              : isRequired
                ? isDark
                  ? 'var(--mantine-color-yellow-6)'
                  : '#ff9800'
                : isDark
                  ? 'var(--mantine-color-dark-4)'
                  : '#ececec';
            e.currentTarget.style.backgroundColor = isDark ? 'var(--mantine-color-dark-6)' : '#fff';
          }
        }}
        onMouseDown={(e) => {
          if (!isDragging) {
            e.currentTarget.style.transform = 'translateY(0px)';
            e.currentTarget.style.boxShadow = isDark
              ? '0 4px 15px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15)'
              : '0 4px 15px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.05)';
          }
        }}
        onMouseUp={(e) => {
          if (!isDragging) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isDark
              ? '0 8px 30px rgba(0, 0, 0, 0.4), 0 4px 15px rgba(0, 0, 0, 0.2)'
              : '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 15px rgba(0, 0, 0, 0.08)';
          }
        }}
      >
        {userHas && (
          <IconCheck
            size={18}
            style={{
              color: isDark ? 'var(--mantine-color-teal-5)' : '#4caf50',
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 2,
              borderRadius: '50%',
            }}
          />
        )}
        {onEdit && (
          <ActionIcon
            aria-label={t('common.edit')}
            size="sm"
            variant="light"
            color="gray"
            style={{
              position: 'absolute',
              right: 8,
              top: userHas ? 32 : 8,
              zIndex: 2,
              opacity: 1,
              transform: 'scale(1) rotate(0deg)',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              pointerEvents: 'auto',
            }}
            onClick={handleEditClick}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1) rotate(0deg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
            }}
          >
            <IconPencil size={14} />
          </ActionIcon>
        )}
        {onRequestReview && (
          <IconSend
            size={18}
            style={{
              color: isDark ? 'var(--mantine-color-gray-3)' : '#666',
              position: 'absolute',
              right: 8,
              top: userHas ? (onEdit ? 56 : 32) : onEdit ? 32 : 8,
              zIndex: 2,
              background: isDark ? 'var(--mantine-color-dark-6)' : '#fff',
              borderRadius: '50%',
              cursor: isDragDropEnabled ? 'pointer' : 'default',
              padding: 2,
              opacity: isDragDropEnabled ? 1 : 0,
              transform: isDragDropEnabled ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(-15deg)',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              pointerEvents: isDragDropEnabled ? 'auto' : 'none',
            }}
            onClick={isDragDropEnabled ? handleRequestReviewClick : undefined}
            onMouseEnter={(e) => {
              if (isDragDropEnabled) {
                e.currentTarget.style.color = isDark ? 'var(--mantine-color-blue-4)' : '#2196f3';
              }
            }}
            onMouseLeave={(e) => {
              if (isDragDropEnabled) {
                e.currentTarget.style.color = isDark ? 'var(--mantine-color-gray-3)' : '#666';
              }
            }}
          />
        )}
        <Stack gap={0}>
          <Text fw={500} style={{ fontSize: 13, lineHeight: 1.3, paddingRight: 22 }}>
            {comp.title}
          </Text>

          {userCompetency && (
            <Text
              size="xs"
              c={isDark ? 'teal.4' : 'teal'}
              style={{ fontWeight: 500, marginTop: 4 }}
            >
              {t('competencyCard.acquired')}:{' '}
              {formatDateShort(userCompetency.acquiredDate, i18n.language)}
            </Text>
          )}

          {comp.description && (
            <div
              style={{
                overflow: 'hidden',
                transition: 'all 0.3s ease-in-out',
                maxHeight: showDescription ? '200px' : '0px',
                opacity: showDescription ? 1 : 0,
                marginTop: showDescription ? 4 : 0,
                marginBottom: showDescription ? 4 : 0,
              }}
            >
              <Text size="xs" c={isDark ? 'gray.4' : 'dimmed'} style={{ lineHeight: 1.3 }}>
                {comp.description}
              </Text>
            </div>
          )}

          {comp.tags && comp.tags.length > 0 && (
            <div
              style={{
                overflow: 'hidden',
                transition: 'all 0.3s ease-in-out',
                maxHeight: showTags ? '50px' : '0px',
                opacity: showTags ? 1 : 0,
                marginTop: showTags ? 4 : 0,
              }}
            >
              <Group gap={4}>
                {comp.tags.slice(0, 2).map((tag) => (
                  <Badge
                    key={tag._id}
                    size="xs"
                    variant={isDark ? 'filled' : 'light'}
                    color={isDark ? 'gray.7' : 'gray'}
                  >
                    {tag.name}
                  </Badge>
                ))}
                {comp.tags.length > 2 && (
                  <Badge
                    size="xs"
                    variant={isDark ? 'filled' : 'light'}
                    color={isDark ? 'gray.7' : 'gray'}
                  >
                    +{comp.tags.length - 2}
                  </Badge>
                )}
              </Group>
            </div>
          )}

          {userCompetency?.source && userCompetency.source !== 'review' && (
            <Group gap={4} mt={6}>
              <Badge size="xs" variant="light" color="gray">
                {t(`profile.competencies.sourceBadge.${userCompetency.source}`)}
              </Badge>
            </Group>
          )}
        </Stack>
      </Paper>
    );
  },
);

export const CompetencyCard = React.memo(CompetencyCardComponent);
